import { useRef, useEffect } from 'react';
import { ThreeEvent, useLoader } from '@react-three/fiber';
import { SceneState, useSceneStore } from '../../state/sceneStore';
import { useZScale } from './SceneCanvas';
import * as THREE from 'three';

interface WallDrawingOverlayProps {
  imageUrl: string;
}

const WallDrawingOverlay: React.FC<WallDrawingOverlayProps> = ({ imageUrl }) => {
  const meshRef = useRef<THREE.Mesh>(null);
  const texture = useLoader(THREE.TextureLoader, imageUrl);
  const zScale = useZScale();

  // Track mouse position for drag detection
  const mouseDownPos = useRef<{ x: number; y: number } | null>(null);
  const clickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isDoubleClickRef = useRef(false);

  const isWallDrawingMode = useSceneStore((state: SceneState) => state.isWallDrawingMode);
  const currentWallPoints = useSceneStore((state: SceneState) => state.currentWallPoints);
  const setCurrentWallPoints = useSceneStore((state: SceneState) => state.setCurrentWallPoints);
  const wallThickness = useSceneStore((state: SceneState) => state.wallThickness);
  const finishCurrentWall = useSceneStore((state: SceneState) => state.finishCurrentWall);
  const clearCurrentWall = useSceneStore((state: SceneState) => state.clearCurrentWall);
  const referenceImageConfig = useSceneStore((state: SceneState) => state.referenceImageConfig);

  // Calculate the same positioning as ReferenceImagePlane
  const aspect = texture.image ? texture.image.width / texture.image.height : 1;
  const planeWidth = 20;
  const planeHeight = planeWidth / aspect;

  let position: [number, number, number] = [0, 0, 0.07 * zScale]; // Slightly above reference image
  let rotation: [number, number, number] = [0, 0, 0];
  let scale: [number, number, number] = [1, 1, 1];

  if (referenceImageConfig.origin && texture.image) {
    const { width: imgWidth, height: imgHeight } = texture.image;

    if (
      referenceImageConfig.scalePointA &&
      referenceImageConfig.scalePointB &&
      referenceImageConfig.scaleDistance &&
      referenceImageConfig.scaleDistance > 0
    ) {
      const [ax, ay] = referenceImageConfig.scalePointA;
      const [bx, by] = referenceImageConfig.scalePointB;
      const pixelDistance = Math.sqrt((ax - bx) ** 2 + (ay - by) ** 2);

      if (pixelDistance > 0) {
        const metersPerPixel = referenceImageConfig.scaleDistance / pixelDistance;
        const planeWidthInMeters = imgWidth * metersPerPixel;
        const scaleFactor = planeWidthInMeters / planeWidth;
        scale = [scaleFactor, scaleFactor, 1];
      }
    }

    const [originX, originY] = referenceImageConfig.origin;

    let angle = 0;
    if (referenceImageConfig.xAxisEnd) {
      const [xAxisEndX, xAxisEndY] = referenceImageConfig.xAxisEnd;
      const dx = xAxisEndX - originX;
      const dy = xAxisEndY - originY;
      angle = -Math.atan2(-dy, dx);
    }
    rotation = [0, 0, angle];

    // The 3D coordinate of the origin pixel on the plane, relative to the plane's center
    const origin3DX = (originX / imgWidth - 0.5) * planeWidth;
    const origin3DY = (0.5 - originY / imgHeight) * planeHeight;

    // We want the origin pixel to be at world (0,0,0).
    // The plane is rotated around its center. We need to adjust its position
    // so that after rotation, the origin pixel lands on (0,0,0).
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const rotatedOriginX = origin3DX * cos - origin3DY * sin;
    const rotatedOriginY = origin3DX * sin + origin3DY * cos;

    position = [-rotatedOriginX * scale[0], -rotatedOriginY * scale[1], 0.07 * zScale];
  }

  const handleClick = (event: ThreeEvent<MouseEvent>) => {
    if (!isWallDrawingMode || !meshRef.current) return;

    event.stopPropagation();

    // Check if this was a drag (mouse moved significantly)
    if (mouseDownPos.current) {
      const deltaX = Math.abs(event.clientX - mouseDownPos.current.x);
      const deltaY = Math.abs(event.clientY - mouseDownPos.current.y);
      const dragThreshold = 5; // pixels

      if (deltaX > dragThreshold || deltaY > dragThreshold) {
        console.log('Drag detected, not adding wall point');
        mouseDownPos.current = null;
        return;
      }
    }

    // Delay the single click to allow double-click to cancel it
    clickTimeoutRef.current = setTimeout(() => {
      // Check if this was actually a double-click
      if (isDoubleClickRef.current) {
        console.log('Single click cancelled due to double-click');
        isDoubleClickRef.current = false;
        mouseDownPos.current = null;
        return;
      }

      console.log('Wall drawing click detected!', { isWallDrawingMode, meshRef: meshRef.current });

      // Get intersection point
      const intersection = event.intersections.find(i => i.object === meshRef.current);
      if (!intersection) {
        console.log('No intersection found');
        mouseDownPos.current = null;
        return;
      }

      const point = intersection.point;
      const newPoint: [number, number] = [point.x, point.y];

      console.log('Adding wall point:', newPoint);
      setCurrentWallPoints([...currentWallPoints, newPoint]);
      mouseDownPos.current = null;
    }, 200); // 200ms delay to allow double-click detection
  };


  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    if (!isWallDrawingMode) return;

    event.stopPropagation();

    // Set flag to prevent single click from executing
    isDoubleClickRef.current = true;

    // Cancel any pending single click
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
      clickTimeoutRef.current = null;
    }

    console.log('Double click detected, finishing wall with', currentWallPoints.length, 'points');

    if (currentWallPoints.length >= 2) {
      finishCurrentWall();
    } else {
      console.log('Not enough points to finish wall (need at least 2)');
    }
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (!isWallDrawingMode) return;

    // Reset double-click flag for new click sequence
    isDoubleClickRef.current = false;

    // Store mouse position for drag detection
    if (event.button === 0) { // Left mouse button
      mouseDownPos.current = { x: event.clientX, y: event.clientY };
    }

    // Only handle middle mouse button for finishing walls
    if (event.button === 1) { // Middle mouse button
      event.stopPropagation();
      console.log('Middle click detected, finishing wall with', currentWallPoints.length, 'points');
      if (currentWallPoints.length >= 2) {
        finishCurrentWall();
      } else {
        console.log('Not enough points to finish wall (need at least 2)');
      }
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (!isWallDrawingMode) return;

      switch (event.key) {
        case 'Enter':
          if (currentWallPoints.length >= 2) {
            finishCurrentWall();
          }
          break;
        case 'Escape':
          clearCurrentWall();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
      // Cleanup timeout on unmount
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
      }
    };
  }, [isWallDrawingMode, currentWallPoints.length, finishCurrentWall, clearCurrentWall]);

  return (
    <group>
      {/* Huge plane for catching clicks anywhere and providing visual feedback */}
      {isWallDrawingMode && (
        <mesh
          ref={meshRef}
          position={[0, 0, 0.07 * zScale]}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          onPointerDown={handlePointerDown}
        >
          <planeGeometry args={[10000, 10000]} />
          <meshBasicMaterial transparent opacity={0.1} color="red" depthWrite={false} />
        </mesh>
      )}

      {/* Draw current wall being drawn */}
      {currentWallPoints.length > 0 && (
        <group>
          {/* Draw wall segments */}
          {currentWallPoints.map((point, index) => {
            if (index === 0) return null;

            const prevPoint = currentWallPoints[index - 1];
            const start = new THREE.Vector3(prevPoint[0], prevPoint[1], position[2] + 0.01);
            const end = new THREE.Vector3(point[0], point[1], position[2] + 0.01);

            // Calculate the center point and rotation for the cylinder
            const center = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
            const direction = new THREE.Vector3().subVectors(end, start).normalize();
            const length = start.distanceTo(end);

            // Calculate rotation to align cylinder with the line
            const angle = Math.atan2(direction.y, direction.x);

            return (
              <mesh
                key={`segment-${index}`}
                position={[center.x, center.y, center.z]}
                rotation={[0, 0, angle]}
              >
                <boxGeometry args={[length, wallThickness / 2, 0.05]} />
                <meshBasicMaterial color="red" />
              </mesh>
            );
          })}

          {/* Draw points */}
          {currentWallPoints.map((point, index) => (
            <mesh key={`point-${index}`} position={[point[0], point[1], position[2] + 0.01]}>
              <sphereGeometry args={[wallThickness / 3, 8, 8]} />
              <meshBasicMaterial color="red" />
            </mesh>
          ))}
        </group>
      )}
    </group>
  );
};

export default WallDrawingOverlay;
